/* eslint-disable no-nested-ternary */
import React, { SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles, Tooltip } from "@material-ui/core";
import { Map as ImmutableMap } from 'immutable';
import { People, PeopleOutline } from "@material-ui/icons";
import { AgentInfo, WidgetMetric } from "./QueueWidgetContainer";
import RcButton from "./UiUtilities/Button";

const DonutSmall = ({ mode, answered, abandoned, voicemail, missed }: { mode: string; answered: number; abandoned: number; voicemail: number; missed: number }) => {
  const total = answered + abandoned + voicemail + missed;

  const answeredPercentage = total === 0 ? 0 : (answered / total) * 360;
  const abandonedPercentage = total === 0 ? 0 : (abandoned / total) * 360;
  const voicemailPercentage = total === 0 ? 0 : (voicemail / total) * 360;
  const missedPercentage = total === 0 ? 0 : (missed / total) * 360;
  return (
    <div
      className="ex-queue-call-data-donut"
      style={{
        height: 75,
        minHeight: 75,
        maxHeight: 75,
        minWidth: 75,
        maxWidth: 75,
        width: 75,
        position: "relative",
        fontSize: 16,
        paddingRight: 15,
        paddingTop: 25,
        display: "inline-block",
      }}
    >
      <div
        className="widget-call-chart"
        style={{
          borderRadius: 50,
          background: `conic-gradient(
          green 0deg ${answeredPercentage}deg,
          brown ${answeredPercentage}deg ${answeredPercentage + abandonedPercentage}deg,
        #35baf6 ${answeredPercentage + abandonedPercentage}deg ${answeredPercentage + abandonedPercentage + voicemailPercentage}deg,
          purple ${answeredPercentage + abandonedPercentage + voicemailPercentage}deg ${answeredPercentage + abandonedPercentage + voicemailPercentage + missedPercentage}deg
        )`,
        }}
      >
        <div
          className="widget-call-overlay"
          style={{
            background: mode === "light" ? "#f5f5f5" : "#0e0e0e",
          }}
        >
          <h5 style={{ marginBottom: 0, marginTop: 0, fontSize: 20 }}>
            {answered +
              abandoned +
              voicemail +
              missed}
          </h5>
          <span style={{ fontWeight: "bold", fontSize: 14 }}>
            {/* {
              (findSize(answered) +
              findSize(abandoned) +
              findSize(voicemail) +
              findSize(missed)) === 0 || 
              (findSize(answered) +
              findSize(abandoned) +
              findSize(voicemail) +
              findSize(missed)) > 1 ? "Calls" : "Call"
            } */}
            Calls
          </span>
        </div>
      </div>
    </div>

  )
}

const Donut = ({ users, status, available, busy, notAccepting, mode }: { users: any, status: any, available: number, busy: number, notAccepting: number, mode: string }) => {
  const total = users.length;

  const availableDegrees = total === 0 ? 0 : (available / total) * 360;
  const busyDegrees = total === 0 ? 0 : (busy / total) * 360;
  const notAcceptingDegrees = total === 0 ? 0 : (notAccepting / total) * 360;
  const unknownDegrees = total === 0 ? 0 : ((total - available - busy - notAccepting) / total) * 360;

  return (
    <Tooltip
      title={users.map((user: any, index: number) => (
      <div key={user} style={{ display: "flex", alignItems: "center", gap: "3px"}}>
        <span style={{
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          display: 'inline-block',
          backgroundColor: status[index] === 'available' ? 'green' : status[index] === 'busy' ? 'pink' : 'brown',
        }} />
        <span>{user}</span>
      </div>
      ))}
      placement="right-start"
    >
      <div
        className="ex-agent-data-donut"
        style={{
          height: 90,
          width: 90,
          minWidth: 90,
          position: "relative",
          fontSize: 16,
          display: "inline-block",
        }}
      >
        <div
          className="widget-call-chart larger-chart"
          style={{
            position: 'absolute',
            top: -14,
            border: mode === 'light' ? "2px solid white" : "1px solid rgb(86, 86, 86)",
            borderRadius: 50,
            background: `conic-gradient(
          green 0deg ${availableDegrees}deg,
          pink ${availableDegrees}deg ${availableDegrees + busyDegrees}deg,
          brown ${availableDegrees + busyDegrees}deg ${availableDegrees + busyDegrees + notAcceptingDegrees + unknownDegrees}deg
        )`,
          }}
        >
          <div
            className="widget-call-overlay larger-overlay"
            style={{
              background: mode === "light" ? "#f5f5f5" : "#0e0e0e",
            }}
          >
            <h5
              style={{
                marginBottom: 0,
                marginTop: 0,
                fontSize: 20,
              }}
            >
              {total}
            </h5>
            <span style={{ fontWeight: "bold", fontSize: 13 }}>
              Agents
            </span>
          </div>
        </div>
      </div>
    </Tooltip>
  )
}

const Widget = (({
  queueName,
  waiting,
  inProgress,
  answered,
  abandoned,
  voicemail,
  missed,
  sidebar,
  mode,
  legacy,
  toggled,
  handleQueueToggle,
  agents,
  available,
  busy,
  unavailable,
  extension,
  queueId,
  excluded,
  queueCompactView
}: {
  excluded: boolean,
  sidebar: boolean,
  extension: number,
  queueName: string;
  waiting: {
    callId: string;
    timestamp: Date;
    phoneNumber: string;
  }[];
  inProgress: number;
  answered: WidgetMetric;
  abandoned: WidgetMetric;
  voicemail: WidgetMetric;
  missed: WidgetMetric;
  mode: string;
  legacy: boolean;
  toggled: boolean;
  handleQueueToggle: (queue: string) => void,
  agents: {
    name: string;
    id: string;
    displayStatus: string;
    availableAccepting: boolean,
    busyAccepting: boolean,
    notAccepting: boolean,
  }[],
  available: number,
  busy: number,
  unavailable: number,
  queueId: string,
  queueCompactView: boolean;
}) => {
  // Calculate the longest wait time
  const [longestWait, setLongestWait] = useState("- : - : -");
  
  const calculateAverage = useCallback((item: WidgetMetric) => {
    if(item.weight === 0) {
      return "--:--";
    } 
    const duration = item.duration / item.weight;
    const seconds = Math.floor((duration / 1000) % 60);
    const minutes = Math.floor((duration / (1000 *60)));
    const outNumber = seconds === 0 && minutes === 0 ? "--:--" : `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    return outNumber.startsWith('0') ? outNumber.slice(1) : outNumber;
  }, []);

  useEffect(() => {
    if (waiting.length === 0) {
      // Clear the longest wait and ensure no interval runs
      setLongestWait("- : - : -");
      return;
    }

    const calculateLongestWait = () => {
      const now = Date.now();
      const longestWaitTime = Math.max(
        ...waiting.map((call) => now - new Date(call.timestamp).getTime())
      );

      const seconds = Math.floor((longestWaitTime / 1000) % 60);
      const minutes = Math.floor((longestWaitTime / (1000 * 60)) % 60);
      const hours = Math.floor(longestWaitTime / (1000 * 60 * 60));

      setLongestWait(
        `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
      );
    };

    // Run immediately and set up interval
    calculateLongestWait();
    const interval = setInterval(calculateLongestWait, 1000);

    // Cleanup interval on unmount or when waiting becomes empty
    // eslint-disable-next-line consistent-return
    return () => clearInterval(interval);
  }, [waiting]);
  
  if(excluded) {
    return null;
  }

  return (
    <div className={`queue-widget-item legacy ${sidebar ? 'sidebar-open' : ""} ${mode === 'dark' ? "dark" : "light"}`}>
      <div className="widget-item-header">
        <div className="widget-header-left">
          <div className="widget-header-text primary-text notranslate">{queueName}</div>
          <div className="widget-header-text subtext notranslate">{extension}</div>
        </div>
        <div className="widget-header-right">
          <RcButton size="sm" bg={mode === 'light' ? (toggled ? "#ccc" : "") : (toggled ? "#555" : "")} color={mode === 'light' ? "black" : "white"} style={{ borderRadius: '50%', height: 35 }} text={!toggled ? "Show Agents" : "Hide Agents"} onClick={handleQueueToggle}>
            {toggled ? <People /> : <PeopleOutline />}
          </RcButton>
        </div>
      </div>
      <div className={`queue-widget-body ${mode === "light" ? "light" : "dark"} ${waiting.length > 0 ? "red-strip" : "green-strip"}`}>
        <div className="widget-pie-container">
          <Donut
            users={agents.map((agent) => agent.name)}
            status={agents.map((agent) => {
              if (agent.availableAccepting) return "available";
              if (agent.busyAccepting) return "busy";
              return "unavailable";
            })}
            available={available}
            busy={busy}
            notAccepting={unavailable}
            mode={mode}
          />
          <div className="widget-body-detail">
            <div className="widget-body-column">
              <div className="widget-body-detail-row">
                <span className="widget-body-detail-indicator available" />
                <span>Available: {available}</span>
              </div>
              <div className="widget-body-detail-row">
                <span className="widget-body-detail-indicator busy" />
                <span>Busy: {busy}</span>
              </div>
              <div className="widget-body-detail-row">
                <span className="widget-body-detail-indicator unavailable" />
                <span>Unavailable: {unavailable + (agents.length - available - busy - unavailable)}</span>
              </div>
              {/* <div className="widget-body-detail-row">
                <span className="widget-body-detail-indicator unknown" />
                <span>Unknown: {agents.length - available - busy - unavailable}</span>
              </div> */}
            </div>
            <div className="widget-body-column">
              <div className="widget-body-detail-row">
                <span>In Progress: {inProgress}</span>
              </div>
              <div className="widget-body-detail-row">
                <span>Waiting: {waiting.length}</span>
              </div>
              <div className="widget-body-detail-row">
                <span>Longest: {longestWait}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={`queue-widget-footer ${mode === 'light' ? "light" : "dark"}`}>
        <div className="widget-footer-left">
          <DonutSmall mode={mode}
            answered={answered.weight} abandoned={abandoned.weight} voicemail={voicemail.weight} missed={missed.weight}
          />
        </div>
        <div className="widget-footer-right">
          <div className="widget-chart-header">

            <div className="widget-chart-header-text title-text">Result</div>
            <div className="widget-chart-header-text title-text">Total</div>
            <div className="widget-chart-header-text title-text">%</div>
            <div className="widget-chart-header-text title-text">Avg. Wait</div>

            <div className="widget-chart-header-text"><span className="widget-body-detail-indicator available" /><span style={{whiteSpace: "nowrap", overflow: 'hidden', textOverflow: "ellipsis"}}>Answered</span></div>
            <div className="widget-chart-header-text">{answered.weight}</div>
            <div className="widget-chart-header-text">{answered.weight ? `${Math.floor(answered.weight / ((answered.weight + abandoned.weight + missed.weight + voicemail.weight) || 1) * 100)}%` : "--"}</div>
            <div className="widget-chart-header-text">{calculateAverage(answered)}</div>

            <div className="widget-chart-header-text"><span className="widget-body-detail-indicator unavailable" /><span style={{whiteSpace: "nowrap", overflow: 'hidden', textOverflow: "ellipsis"}}>Abandoned</span></div>
            <div className="widget-chart-header-text">{abandoned.weight}</div>
            <div className="widget-chart-header-text">{abandoned.weight ? `${Math.floor(abandoned.weight / ((answered.weight + abandoned.weight + missed.weight + voicemail.weight) || 1) * 100)}%` : "--"}</div>
            <div className="widget-chart-header-text">{calculateAverage(abandoned)}</div>

            <div className="widget-chart-header-text"><span className="widget-body-detail-indicator voicemail" /><span style={{whiteSpace: "nowrap", overflow: 'hidden', textOverflow: "ellipsis"}}>Voicemail</span></div>
            <div className="widget-chart-header-text">{voicemail.weight}</div>
            <div className="widget-chart-header-text">{voicemail.weight ? `${Math.floor(voicemail.weight / ((answered.weight + abandoned.weight + missed.weight + voicemail.weight) || 1) * 100)}%`: "--"}</div>
            <div className="widget-chart-header-text">{calculateAverage(voicemail)}</div>

            <div className="widget-chart-header-text"><span className="widget-body-detail-indicator other"/><span style={{whiteSpace: "nowrap", overflow: 'hidden', textOverflow: "ellipsis"}}>Other</span></div>
            <div className="widget-chart-header-text">{missed.weight}</div>
            <div className="widget-chart-header-text">{missed.weight ? `${Math.floor(missed.weight / ((answered.weight + abandoned.weight + missed.weight + voicemail.weight) || 1) * 100)}%` : "--"}</div>
            <div className="widget-chart-header-text">{calculateAverage(missed)}</div>
            
          </div>
        </div>
      </div>
    </div>
  );
})

export default Widget;
